import React, { useState, useEffect } from 'react';
import { AppBar, Tabs, Tab, Box, MenuItem, Menu } from '@material-ui/core';
import DocumentContainer from '../DocumentContainer';
import TabContainer from 'global/components/TabContainer';
import useStyles from './DocumentTabs.styles';
import { getLabel, getIcon, FILEADD, VIDEOADD, ADDTAB } from '../definitions';
import { getUrlParams } from 'global/utils';
import { get, first } from 'global/lib/lodash';
import { useScreen } from 'global/styles';
import withRouter from 'react-router-dom/withRouter';
import { useEdit } from '../../../global/hooks/useEdit';

export default withRouter((props) => {
  const { documents, userCanEdit } = props;
  const [currentTab, setCurrentTab] = useState('');
  const { isMobile } = useScreen();
  const classes = useStyles({ ...props, isMobile });
  const { editOn } = useEdit();

  const hasFilePerms = userCanEdit;

  useEffect(() => {
    const { search } = props.location;
    const { activeTab } = getUrlParams(search);

    const isAdd = ADDTAB.children.find((f) => f.id == activeTab);

    if (activeTab && isAdd) {
      const hasFile = (documents || []).find((f) => f.documentableType == isAdd.documentableType);
      if (hasFile) {
        setCurrentTab(get(hasFile, 'id'));
      } else {
        setCurrentTab(activeTab);
      }
    } else if (activeTab) {
      setCurrentTab(activeTab);
    } else {
      const primaryDoc = (documents || []).find((d) => !!d.primaryDocument);
      setCurrentTab(get(primaryDoc, 'id', get(documents, '0.id')));
    }
  }, [documents]);

  const changeLocation = (d) => {
    const { replace } = props.history;
    const { pathname, search } = props.location;

    let activeTab = d.id;

    const isAdd = ADDTAB.children.find((f) => f.id == activeTab);
    if (isAdd) {
      const hasFile = (documents || []).find((f) => f.documentableType == isAdd.documentableType);
      if (hasFile && get(isAdd, 'shouldCheckForMatches', true)) {
        activeTab = hasFile.id;
      }
    }

    const obj = {
      ...getUrlParams(search),
      activeTab,
    };

    const str = new URLSearchParams(obj).toString();

    if (!props.isModal) {
      replace(`${pathname}?${str}`);
    }

    setCurrentTab(activeTab);
  };

  const getTabs = () => {
    const nonFiles = (documents || []).filter((d) => d.documentableType !== FILEADD.documentableType && d.documentableType !== VIDEOADD.documentableType);
    const hasVideo = (documents || []).find((d) => d.documentableType === VIDEOADD.documentableType);
    const hasFile = (documents || []).find((d) => d.documentableType === FILEADD.documentableType);

    //only want one files tab, so we are grabbing the first, or a "create new" tab
    const files = [...(hasFile ? [hasFile] : hasFilePerms ? [] : []), ...(hasVideo ? [hasVideo] : hasFilePerms ? [] : [])];
    return [...nonFiles, ...files, hasFilePerms && editOn && ADDTAB].filter((f) => !!f);
  };

  const getDoc = () => {
    const _doc = (documents || []).find((d) => d.id === currentTab);

    const editTab = ADDTAB.children.find((c) => c.id === currentTab);

    if (editTab) {
      return [
        {
          ...editTab,
          classification: get(first(documents), 'classification'),
          classificationId: get(first(documents), 'classificationId'),
          editionId: get(first(documents), 'editionId'),
        },
      ];
    }

    if (!props.isModal && _doc && _doc.documentableType === FILEADD.documentableType) {
      return (documents || [])
        .filter((d) => d.documentableType === FILEADD.documentableType)
        .map((d) => ({
          ...FILEADD,
          ...d,
        }));
    } else if (!props.isModal && _doc && _doc.documentableType === VIDEOADD.documentableType) {
      return (documents || [])
        .filter((d) => d.documentableType === VIDEOADD.documentableType)
        .map((d) => ({
          ...VIDEOADD,
          ...d,
        }));
    }

    return _doc;
  };

  const update = async () => {
    await Promise.all([await props.update(), await get(window, 'DOC_LIST_EDITION.update', () => 0)()]);
  };

  const doc = getDoc();

  useEffect(() => {
    if (!!doc) {
      props.onDoc && props.onDoc(doc);
    }
  }, [doc]);

  return (
    <div className={classes.root}>
      <Box displayPrint="none">
        <AppBar position="static">
          <Tabs value={currentTab} size="small" variant="scrollable" className={classes.tab}>
            {getTabs().map((d) => (
              <DocTab {...d} onClick={changeLocation} currentTab={currentTab} />
            ))}
          </Tabs>
        </AppBar>
      </Box>
      <TabContainer>
        <DocumentContainer {...props} update={update} doc={doc} className={classes.content} />
      </TabContainer>
    </div>
  );
});

export const DocTab = (props) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const { documentableType, id, currentTab } = props;

  const onClick = (e) => {
    if (id !== ADDTAB.id) {
      return props.onClick(props);
    }

    setAnchorEl(e.currentTarget);
  };

  const onClickChild = (e, child) => {
    setAnchorEl(null);
    props.onClick(child);
  };

  return (
    <>
      <Tab icon={getIcon(documentableType)} selected={currentTab == id} label={getLabel(documentableType, props)} value={id} onClick={onClick} aria-controls="customized-menu" />
      {!!props.children && (
        <Menu
          id="customized-menu"
          open={open}
          anchorEl={anchorEl}
          style={{ top: 50 }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          onClose={() => setAnchorEl(null)}
        >
          {props.children.map((child) => (
            <MenuItem onClick={(e) => onClickChild(e, child)}>{getLabel(child.documentableType, child)}</MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
};
